import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useCurrentCompany } from './useCurrentCompany';

export interface TopStudent {
  student_id: string;
  sport_id: string;
  sport_name: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  attendance_count: number;
  period_start: string;
  rank: number;
}

export function useTopStudents(
  sportId: string | null = null,
  period: 'week' | 'month' | 'quarter' | 'year' = 'week',
  limit: number = 10
) {
  const { data: companyId } = useCurrentCompany();

  return useQuery({
    queryKey: ['top-students', companyId, sportId, period, limit],
    queryFn: async () => {
      if (!companyId) return [];

      try {
        const { data, error } = await supabase.rpc('get_top_students_by_sport' as any, {
          p_company_id: companyId,
          p_sport_id: sportId || null,
          p_period: period,
          p_limit: limit,
        });

        if (error) {
          // If function doesn't exist (migration not applied), return empty array gracefully
          if (error.code === 'PGRST202' || error.message?.includes('does not exist') || error.message?.includes('function')) {
            console.warn('get_top_students_by_sport function not found. Migrations may not be applied.');
            return [];
          }
          console.error('Error fetching top students:', error);
          throw error;
        }

        return (data || []) as TopStudent[];
      } catch (error: any) {
        // Handle function not found gracefully
        if (error?.code === 'PGRST202' || error?.message?.includes('does not exist') || error?.message?.includes('function')) {
          console.warn('get_top_students_by_sport function not found. Migrations may not be applied.');
          return [];
        }
        throw error;
      }
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
